<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { LogOut, Sun, Moon, Menu, X } from '@lucide/vue'
import { Button, Card } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useAdminAuth } from '../composables/useAdminAuth.js'
import { THEME_DARK, useAdminTheme } from '../composables/useAdminTheme.js'
import { ADMIN_NAV_GROUPS } from './admin-nav.js'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const auth = useAdminAuth()
const { theme, toggle: toggleTheme } = useAdminTheme()

// 图标画的是「点了会变成什么」:暗色时给太阳(点了开灯)。
const dark = computed(() => theme.value === THEME_DARK)
const isFilmReview = computed(() => route.path === '/story-film/prompt-review')
const reviewNavOpen = ref(false)
watch(() => route.path, () => { reviewNavOpen.value = false })

/**
 * LLM 后台只对 admin 开放(候补名单是 admin|operator)。导航对非 admin 隐藏,是为了不让人点进去
 * 撞一屏 403 —— 真正的权限在后端的 `requireEditorRole(request, services, ['admin'])` 和路由层的
 * `LlmSection` 守卫上。
 */
const isAdmin = computed(() => auth.account.value?.role === 'admin')
const navGroups = computed(() => ADMIN_NAV_GROUPS.filter(group => !group.adminOnly || isAdmin.value))

/**
 * 高亮项自己算，不用 RouterLink 的 router-link-active：详情页（/users/:id、/intent-videos/queue/:id）
 * 和列表页是平级路由，vue-router 按匹配记录判断时列表项不会亮。这里按路径前缀取最长匹配，
 * /analytics/content 只亮「内容互动」，不会连「总览」一起亮。
 */
const activeLinkPath = computed(() => {
  let best = ''
  for (const group of navGroups.value) {
    for (const link of group.links) {
      const matches = route.path === link.to || route.path.startsWith(`${link.to}/`)
      if (matches && link.to.length > best.length) best = link.to
    }
  }
  return best
})

async function logout() {
  await auth.logout()
  router.replace({ name: 'login' })
}
</script>

<template>
  <div class="admin-shell" :class="{ 'is-film-review': isFilmReview }">
    <aside class="admin-shell__rail">
      <div class="admin-shell__brand">
        <span>VG</span>
        <strong>{{ t('visualGameAdmin.app.name') }}</strong>
      </div>
      <button v-if="isFilmReview" class="admin-shell__review-nav-toggle" type="button" :aria-label="t('storyFilmReview.navigation')" :aria-expanded="reviewNavOpen" aria-controls="admin-review-nav" @click="reviewNavOpen = !reviewNavOpen">
        <X v-if="reviewNavOpen" :size="20" /><Menu v-else :size="20" />
      </button>
      <nav id="admin-review-nav" class="admin-shell__links" :class="{ 'is-review-open': reviewNavOpen }">
        <template v-for="group in navGroups" :key="group.key">
          <span class="admin-shell__group">{{ t(`visualGameAdmin.app.groups.${group.key}`) }}</span>
          <RouterLink
            v-for="link in group.links"
            :key="link.to"
            class="admin-shell__nav"
            :class="{ 'is-active': link.to === activeLinkPath }"
            :to="link.to"
            @click="reviewNavOpen = false"
          >
            <component :is="link.icon" :size="18" />
            <span>{{ t(link.label) }}</span>
          </RouterLink>
        </template>
        <div v-if="isFilmReview" class="admin-shell__review-actions">
          <Button variant="ghost" size="sm" :aria-label="t(dark ? 'visualGameAdmin.app.theme.toLight' : 'visualGameAdmin.app.theme.toDark')" @click="toggleTheme"><Sun v-if="dark" :size="16" /><Moon v-else :size="16" /></Button>
          <Button variant="ghost" size="sm" @click="logout"><LogOut :size="16" /> {{ t('visualGameAdmin.app.logout') }}</Button>
        </div>
      </nav>
    </aside>
    <div class="admin-shell__content">
      <header class="admin-shell__topbar">
        <div class="admin-shell__account">
          <strong>{{ auth.account.value?.email }}</strong>
          <span>{{ auth.account.value?.role }}</span>
        </div>
        <div class="admin-shell__topbar-actions">
          <Button
            variant="ghost"
            size="sm"
            :title="t(dark ? 'visualGameAdmin.app.theme.toLight' : 'visualGameAdmin.app.theme.toDark')"
            :aria-label="t(dark ? 'visualGameAdmin.app.theme.toLight' : 'visualGameAdmin.app.theme.toDark')"
            @click="toggleTheme"
          >
            <Sun v-if="dark" :size="16" />
            <Moon v-else :size="16" />
          </Button>
          <Button variant="ghost" size="sm" @click="logout">
            <LogOut :size="16" />
            {{ t('visualGameAdmin.app.logout') }}
          </Button>
        </div>
      </header>

      <main class="admin-shell__main">
        <Card v-if="auth.forbidden.value" data-insp-path="apps/visual-game-admin/src/layouts/AdminShell.vue#forbidden">
          <p class="vga-error">{{ t('visualGameAdmin.app.forbidden') }}</p>
        </Card>
        <RouterView v-else />
      </main>
    </div>
  </div>
</template>

<style scoped>
.admin-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  background: var(--drama-color-bg);
}

.admin-shell__rail {
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-xl);
  padding: var(--drama-space-xl);
  border-right: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
}

.admin-shell__brand {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
}

.admin-shell__brand span {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--vgs-line-gold);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-primary);
  font-weight: var(--drama-font-weight-bold);
}

.admin-shell__brand strong {
  font-size: var(--drama-font-size-md);
}

.admin-shell__review-nav-toggle,
.admin-shell__review-actions {
  display: none;
}

.admin-shell__links {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.admin-shell__group {
  margin: var(--drama-space-lg) 0 var(--drama-space-xs);
  padding-left: var(--drama-space-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-medium);
  letter-spacing: 0.08em;
}

.admin-shell__group:first-child {
  margin-top: 0;
}

.admin-shell__nav {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-left: 2px solid transparent;
  border-radius: var(--drama-radius-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  line-height: var(--drama-line-height-tight);
  transition: color 0.15s ease, background-color 0.15s ease;
}

.admin-shell__nav:hover {
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
}

.admin-shell__nav.is-active {
  border-left-color: var(--drama-color-primary);
  background: var(--vgs-gold-dim);
  color: var(--drama-color-primary);
  font-weight: var(--drama-font-weight-medium);
}

.admin-shell__content {
  min-width: 0;
}

.admin-shell__topbar {
  min-height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--drama-space-lg);
  padding: 0 var(--drama-space-xl);
  border-bottom: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
}

.admin-shell__account {
  display: grid;
  gap: var(--drama-space-xs);
}

.admin-shell__topbar-actions {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}

.admin-shell__topbar span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.admin-shell__main {
  min-width: 0;
  padding: var(--drama-space-xl);
}

@media (max-width: 760px) {
  .admin-shell {
    grid-template-columns: 1fr;
  }

  .admin-shell__rail {
    position: sticky;
    top: 0;
    z-index: 10;
    height: auto;
    overflow-y: visible;
    flex-direction: row;
    align-items: center;
    padding: var(--drama-space-md);
  }

  .admin-shell__links {
    flex-direction: row;
  }

  .admin-shell__brand strong {
    display: none;
  }

  .is-film-review .admin-shell__rail {
    justify-content: space-between;
    min-height: 52px;
    padding: var(--drama-space-xs) var(--drama-space-md);
    overflow: visible;
  }

  .is-film-review .admin-shell__brand span { width: 36px; height: 36px; }
  .is-film-review .admin-shell__review-nav-toggle { display: grid; place-items: center; min-width: 44px; min-height: 44px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface); color: var(--drama-color-text); cursor: pointer; }
  .is-film-review .admin-shell__review-nav-toggle:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
  .is-film-review .admin-shell__links { display: none; }
  .is-film-review .admin-shell__links.is-review-open { position: fixed; z-index: 20; inset: 52px 0 0; display: flex; flex-direction: column; overflow-y: auto; padding: var(--drama-space-md); background: var(--drama-color-surface); }
  .is-film-review .admin-shell__review-actions { display: flex; gap: var(--drama-space-sm); padding: var(--drama-space-md) 0; }
  .is-film-review .admin-shell__topbar { display: none; }
}
</style>
